import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import {
  GoogleMap,
  useJsApiLoader,
  CircleF,
  MarkerF,
  MarkerClustererF
} from '@react-google-maps/api';
import { getGlobalConfig } from '../../utils';
import { useQuery } from '@tanstack/react-query';
import Api from '../API';
import { classes } from './styles';

export default function GoogleMapApplicationLocation({
  jobId,
  onClose
}: {
  jobId: number;
  onClose: () => void;
}) {
  const { googleApiKey } = getGlobalConfig();

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: googleApiKey || ''
  });

  const { data: job } = useQuery({
    queryKey: ['jobLocations', jobId],
    queryFn: async () => await Api.getApplicationLocations(jobId),
    enabled: true
  });

  const applicationIcon = (name: string) => {
    const initials = name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((word) => word[0].toUpperCase())
      .join('');
    return `
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40">
        <!-- Outer white border -->
        <circle cx="20" cy="20" r="20" fill="white" />

        <!-- Inner circle -->
        <circle cx="20" cy="20" r="18" fill="#B9E5E5" />

        <!-- Initials -->
        <text x="50%" y="50%" text-anchor="middle" dy=".35em"
              font-family="Arial, sans-serif"
              font-size="14"
              font-weight="bold"
              fill="#0A3D62">
          ${initials}
        </text>
      </svg>
    `;
  };

  const [map, setMap] = useState<google.maps.Map | null>(null);

  // Fit bounds for job + all applications
  useEffect(() => {
    if (map && job?.latitude && job?.longitude && (job.applications?.length ?? 0) > 0) {
      const bounds = new google.maps.LatLngBounds();
      bounds.extend({ lat: job.latitude, lng: job.longitude });
      job.applications.forEach((app: { latitude: number | null; longitude: number | null }) => {
        if (app.latitude !== null && app.longitude !== null) {
          bounds.extend({ lat: app.latitude, lng: app.longitude });
        }
      });
      map.fitBounds(bounds, {
        top: 50,
        right: 50,
        bottom: 50,
        left: 50
      });
    } else if (map && job) {
      map.setCenter({ lat: job.latitude, lng: job.longitude });
      map.setZoom(12); // fallback if no applications
    }
  }, [map, job]);

  let mainDisplay = <Typography>Loading...</Typography>;
  if (!job) {
    mainDisplay = <Typography>Loading...</Typography>;
  } else if (!job?.latitude || !job?.longitude) {
    mainDisplay = <Typography>Job location is not available</Typography>;
  } else if (job?.applications?.length === 0) {
    mainDisplay = <Typography>No applicant locations available</Typography>;
  } else if (isLoaded) {
    mainDisplay = (
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '300px' }}
        zoom={10}
        onLoad={(m) => setMap(m)}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          styles: [
            { featureType: 'poi', stylers: [{ visibility: 'off' }] }, // remove points of interest to avoid clutter
            { featureType: 'road', elementType: 'geometry', stylers: [{ weight: 0.5 }] } // smaller value = thinner road
          ]
        }}
      >
        <CircleF
          center={{ lat: job.latitude, lng: job.longitude }}
          radius={50000} // 50km
          options={{ fillOpacity: 0, strokeWeight: 1 }}
        />
        <MarkerF
          position={{ lat: job.latitude, lng: job.longitude }}
          zIndex={9999}
          title={job.title}
        />
        <MarkerClustererF options={{ maxZoom: 16 }}>
          {(clusterer) =>
            job.applications
              .filter(
                (app: { latitude: number | null; longitude: number | null }) =>
                  app.latitude !== null && app.longitude !== null
              )
              .map(
                (app: {
                  id: number;
                  latitude: number;
                  longitude: number;
                  candidate_name: string;
                }) => (
                  <MarkerF
                    key={app.id}
                    icon={{
                      url:
                        'data:image/svg+xml;charset=UTF-8,' +
                        encodeURIComponent(applicationIcon(app.candidate_name))
                    }}
                    position={{ lat: app.latitude, lng: app.longitude }}
                    clusterer={clusterer}
                    title={app.candidate_name}
                  />
                )
              )
          }
        </MarkerClustererF>
      </GoogleMap>
    );
  }

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      <CloseIcon onClick={onClose} sx={{ ...classes.closeIcon, top: 0, right: 0, color: '#999' }} />
      <Typography paddingBottom={3} style={classes.applicationLocationHeader}>
        Applicant Locations
      </Typography>

      {mainDisplay}
    </Box>
  );
}
